import { DatabaseOutlined as DataManagementIcon } from '@ant-design/icons'
import { useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'

import { LocalStorageUtility } from '@Utils/storage/StorageUtility'

import ChevronDownIcon from '../../../assets/images/icons/chevron-down.svg?react'
import HelpCenterIcon from '../../../assets/images/icons/help-center-icon.svg?react'
import LogOutIcon from '../../../assets/images/icons/log-out-icon.svg?react'
import PrisyncLogo from '../../../assets/images/icons/prisync-logo.svg?react'
import XIcon from '../../../assets/images/icons/x-icon.svg?react'
import {
  BillingIcon,
  DashboardIcon,
  DynamicPricingIcon,
  ProductIcon,
  ReportsIcon,
  SettingsIcon,
  UrlDashboardIcon,
} from '../../../components/icons/navigation'
import { baseApi } from '../../../store/api'
import { useLogoutMutation } from '../../../store/api/auth'
import { useGetExportCountQuery } from '../../../store/api/product'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { unsetTokens } from '../../../store/slices/authSlice'
import { IntercomEvent } from '../../../utils/helpers/getIntercomMsg'
import useIntercomInternal from '../../../utils/hooks/use-intercom-internal'
import DashboardLink from '../items/dashboard-link'
import ReportLink from '../items/report-link'
import ReportsLink from '../items/reports-link'
import SmartPriceLink from '../items/smart-price-link'
import {
  LogoutButton,
  TrialDaysReminder,
  getDaysLeft,
  getName,
  logoutClick,
} from '../large-menu'
import ReloaderNavlink from '../reloader-navlink'

function LeftMenu({
  toggleMenu,
  isOnboardingCompleted,
  meData,
}: {
  toggleMenu: () => void
  isOnboardingCompleted: boolean
  meData: any
}): JSX.Element {
  const [logout, { isLoading }] = useLogoutMutation()
  const { currentData: exportCount } = useGetExportCountQuery()
  const { trackEventInternal } = useIntercomInternal()
  const refresh_token = useAppSelector((state) => state?.auth.refresh_token)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const handleLogout = (): void => {
    logoutClick({
      refresh_token,
      dispatch,
      trackEventInternal,
      logout,
      storage: new LocalStorageUtility(),
      navigate,
    })
  }

  const displayName = getName(meData)
  const daysLeft = getDaysLeft(meData.trial_end_date)

  const [isReportsOpen, setIsReportsOpen] = useState(false)

  // activeLink color for active NavLink
  const activeLink = {
    backgroundColor: '#F5F7FF',
    color: '#2D3D85',
  }
  return (
    <>
      <div className="fixed bottom-0 top-[--menu-top] transition-all w-full lg:hidden bg-black-100 z-50">
        <div
          className="h-full w-5/6 border-r-2 border-solid border-gray-200 bg-white-100"
          style={{ color: '#344054' }}
        >
          <div className="flex h-full flex-col">
            <div className="flex flex-col">
              <div className="flex flex-row justify-between pt-8 pr-6 pl-4">
                <PrisyncLogo />
              </div>
              <div
                className="pt-8 px-6 flex flex-col space-y-1"
                style={{ color: '#344054' }}
              >
                <DashboardLink
                  to={'/'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                  onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
                >
                  {({ isActive }) => (
                    <div
                      className=" flex flex-row items-center px-2 "
                      onClick={toggleMenu}
                    >
                      <DashboardIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                      <span className="pl-4 ">Dashboard</span>
                    </div>
                  )}
                </DashboardLink>
                <ReloaderNavlink
                  to={'/products'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                  onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
                >
                  {({ isActive }) => (
                    <div
                      className=" flex flex-row items-center px-2 "
                      onClick={toggleMenu}
                    >
                      <ProductIcon stroke={isActive ? '#3E54B8' : '#667085'} />
                      <span className="pl-4 ">Products</span>
                    </div>
                  )}
                </ReloaderNavlink>
                <SmartPriceLink
                  to={'/dynamic-pricing'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                  onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
                >
                  {({ isActive }) => (
                    <div
                      className=" flex flex-row items-center px-2 "
                      onClick={toggleMenu}
                    >
                      <DynamicPricingIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                      <span className="pl-4 ">Dynamic Pricing</span>
                    </div>
                  )}
                </SmartPriceLink>
                <ReloaderNavlink
                  to={'/data-management'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                  onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
                >
                  {({ isActive }) => (
                    <div
                      className=" flex flex-row items-center px-2 "
                      onClick={toggleMenu}
                    >
                      <DataManagementIcon
                        className={`w-6 h-6 flex justify-center [&>svg]:h-6 [&>svg]:w-5 ${
                          isActive
                            ? '[&>path]:stroke-[#3E54B8]'
                            : '[&>path]:stroke-[#667085]'
                        }`}
                      />
                      <span className="pl-4 ">Data Management</span>
                    </div>
                  )}
                </ReloaderNavlink>
                <ReportsLink mobile>
                  <div className="flex flex-col">
                    <button
                      type="button"
                      onClick={() => setIsReportsOpen(!isReportsOpen)}
                      className="flex flex-row items-center justify-between px-2 hover:bg-primary-50 rounded-lg py-2"
                    >
                      <div className="flex flex-row items-center flex-1">
                        <div className="flex flex-row justify-between w-full">
                          <div className="flex flex-row flex-1">
                            <ReportsIcon />
                            <span className="pl-4">Reports</span>
                          </div>
                          {exportCount?.count && exportCount?.count > 0 ? (
                            <div className="rounded-full bg-primary-75 text-sm w-6 h-6 relative mr-2">
                              <span className="absolute w-6 h-6 text-xs leading-6 text-center left-0 top-0">
                                {exportCount?.count}
                              </span>
                            </div>
                          ) : null}
                        </div>
                      </div>
                      <div>
                        <ChevronDownIcon />
                      </div>
                    </button>
                    {isReportsOpen && (
                      <div>
                        <ReportLink
                          to={'/reports/category'}
                          className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2 mb-1"
                          style={({ isActive }) => (isActive ? activeLink : {})}
                          onClick={(e) =>
                            !isOnboardingCompleted && e.preventDefault()
                          }
                          reportType="Category Report"
                        >
                          <div
                            onClick={toggleMenu}
                            className="flex flex-row items-center justify-between px-2"
                          >
                            <div className="flex flex-row items-center px-2">
                              <span className="pl-7">Category Reports</span>
                            </div>
                          </div>
                        </ReportLink>
                        <ReportLink
                          to={'/reports/brand'}
                          className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2 mb-1"
                          style={({ isActive }) => (isActive ? activeLink : {})}
                          onClick={(e) =>
                            !isOnboardingCompleted && e.preventDefault()
                          }
                          reportType="Brand Report"
                        >
                          <div
                            onClick={toggleMenu}
                            className="flex flex-row items-center justify-between px-2"
                          >
                            <div className="flex flex-row items-center px-2">
                              <span className="pl-7">Brand Reports</span>
                            </div>
                          </div>
                        </ReportLink>
                        <NavLink
                          to={'/reports/export'}
                          className=" flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2 mb-1"
                          style={({ isActive }) => (isActive ? activeLink : {})}
                          onClick={(e) =>
                            !isOnboardingCompleted && e.preventDefault()
                          }
                        >
                          <div
                            onClick={toggleMenu}
                            className="flex flex-row items-center justify-between px-2"
                          >
                            <div className="flex flex-row w-full items-center justify-between px-2">
                              <span className="pl-7">Export Reports</span>
                              {exportCount?.count && exportCount?.count > 0 ? (
                                <div className="rounded-full bg-primary-75 text-sm w-6 h-6 relative mr-2">
                                  <span className="absolute w-6 h-6 text-xs leading-6 text-center left-0 top-0">
                                    {exportCount?.count}
                                  </span>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </NavLink>
                      </div>
                    )}
                  </div>
                </ReportsLink>
                {meData?.is_in_trial === 1 && daysLeft > 0 && (
                  <TrialDaysReminder day={daysLeft} />
                )}
              </div>
            </div>
            <div className="flex flex-col h-full justify-end">
              <div className="py-8 px-6 flex flex-col space-y-1">
                <NavLink
                  to={'/settings'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                  onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
                >
                  {({ isActive }) => (
                    <div
                      className=" flex flex-row items-center px-2 "
                      onClick={toggleMenu}
                    >
                      <SettingsIcon stroke={isActive ? '#3E54B8' : '#667085'} />
                      <span className="pl-4 "> Settings</span>
                    </div>
                  )}
                </NavLink>
                <NavLink
                  to={'/404-reports/'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                  onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
                >
                  {({ isActive }) => (
                    <div
                      className=" flex flex-row items-center px-2 "
                      onClick={toggleMenu}
                    >
                      <UrlDashboardIcon
                        stroke={isActive ? '#3E54B8' : '#667085'}
                      />
                      <span className="pl-4 "> URL Dashboard</span>
                    </div>
                  )}
                </NavLink>
                <NavLink
                  to={'/billing'}
                  className="flex flex-col hover:bg-primary-50 border-0 rounded-md justify-center py-2"
                  style={({ isActive }) => (isActive ? activeLink : {})}
                  onClick={(e) => !isOnboardingCompleted && e.preventDefault()}
                >
                  {({ isActive }) => (
                    <div
                      className=" flex flex-row items-center px-2 "
                      onClick={toggleMenu}
                    >
                      <BillingIcon stroke={isActive ? '#3E54B8' : '#667085'} />
                      <span className="pl-4 "> Billing</span>
                    </div>
                  )}
                </NavLink>
                <div
                  className="flex flex-col hover:bg-primary-50 justify-center py-2"
                  onClick={toggleMenu}
                >
                  <a
                    href="https://helpcenter.prisync.com/hc/en-us"
                    target="_blank"
                    rel="noreferrer noopener"
                    className="flex flex-row items-center justify-between pl-2"
                  >
                    <div className="flex flex-row items-center">
                      <HelpCenterIcon />
                      <span className="pl-4">Help Center</span>
                    </div>
                  </a>
                </div>
              </div>
              <ul className="pr-8 pl-4 ">
                <li className="border-t-2 border-gray-200">
                  <LogoutButton onClick={handleLogout} name={displayName} />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      <div className="fixed right-2 top-4 z-50">
        <button
          className="flex flex-row  py-8 items-center"
          onClick={toggleMenu}
        >
          <XIcon />
        </button>
      </div>
    </>
  )
}

export default LeftMenu
